<template>
    <Modal modal-name="registration-modal" modal-class="registration-modal" size="lg" :show-close-button="false" @close="show = false">
        <UiLoader v-if="loading" />
        <div class="registration-modal__body">
            <div
                v-if="$device.isDesktop"
                class="registration-modal__image-wrapper"
                :style="`background-image: url('/nuxt-assets/images/popUp/registration.png')`"
            ></div>
            <div class="registration-modal__main-nwl rounded">
                <div class="d-flex align-items-center">
                    <div class="registration-modal__small-title" v-html="$t('popUp.forNewCustomers')"></div>
                    <div class="ml-auto">
                        <button class="registration-modal__close" @click="hideModal()">&times;</button>
                    </div>
                </div>
                <div class="registration-modal__title" v-html="$t('popUp.getDiscountHtml')"></div>
                <form class="registration-modal__text" @submit.prevent="sentForm">
                    <div class="form-group mb-2">
                        <input
                            v-model="email"
                            class="form-control form-control--silver"
                            type="email"
                            :placeholder="$t('popUp.yourEmail')"
                            autofocus
                            required
                        />
                    </div>

                    <div class="form-group mb-2 d-flex">
                        <input id="registration-form-agree" v-model="agreement" class="form-check-input me-2" type="checkbox" required />
                        <label for="registration-form-agree" class="mt-1">
                            <div>
                                {{ $t('popUp.iAgree') }}
                                <a :href="$t('popUp.termsAndConditionUrl')" class="text-primary text-decoration-underline" target="_blank">
                                    {{ $t('popUp.termsAndCondition') }}
                                </a>
                                {{ $t('popUp.andWith') }}
                                <a :href="$t('popUp.processingDataUrl')" class="text-primary text-decoration-underline" target="_blank">
                                    {{ $t('popUp.processingData') }}
                                </a>
                            </div>
                        </label>
                    </div>

                    <!--                    <div class="form-group mb-2 d-flex">-->
                    <!--                        <input-->
                    <!--                            id="registration-form-newsletter-disagree"-->
                    <!--                            v-model="newsletterDisagree"-->
                    <!--                            class="form-check-input me-2"-->
                    <!--                            type="checkbox"-->
                    <!--                        />-->
                    <!--                        <label for="registration-form-newsletter-disagree" class="mt-1">-->
                    <!--                            <div v-html="$t('popUp.nwlDisagree')"></div>-->
                    <!--                        </label>-->
                    <!--                    </div>-->

                    <button
                        type="submit"
                        class="registration-modal__primary-button w-100"
                        v-html="$t('popUp.registerAndGetDiscount')"
                    ></button>

                    <UiAlert
                        v-if="registerResult && !registerResult.isRegistered"
                        style="background-color: #fff4f2"
                        color="red"
                        borderless
                        text-colored
                    >
                        *{{ registerResult.errors.email }}
                    </UiAlert>
                    <UiAlert v-else-if="registerResult && registerResult.isRegistered" color="green" text-colored>
                        <img src="/nuxt-assets/images/icons/check-green.svg" class="check" width="16" height="16" />
                        <div>{{ $t('popUp.registrationComplete') }}</div>
                    </UiAlert>
                </form>
            </div>
        </div>
    </Modal>
    <div v-if="!show && !footerModal">
        <button class="registration-modal__close-box d-flex" @click="showModal()">
            <div class="mt-1" v-html="$t('popUp.getDiscount')"></div>
            <div class="ico ico-open-modal"></div>
        </button>
    </div>
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import Modal from '~/components/Modal.vue'
import PopUpRegistrationResponse from '~/models/Api/PopUpRegistrationResponse'
import DataLayer from '~/extensions/DataLayer'
import { useRegistrationModalStore } from '~/stores/registrationModal'

const show = ref(false)
const loading = ref(false)
const agreement = ref(false)
const newsletterDisagree = ref(false)
const email = ref('')
const registerResult = ref<PopUpRegistrationResponse | null>(null)
const i18n = useI18n()

const registrationModalStore = useRegistrationModalStore()
const footerModal = computed(() => {
    return registrationModalStore.footerModal
})
const sentForm = async () => {
    loading.value = true
    try {
        registerResult.value = null
        registerResult.value = await useNuxtApp().$repository.validateAndRegisterUser(
            email.value,
            agreement.value,
            newsletterDisagree.value
        )
    } catch (e) {
        console.error(e)
    }
    loading.value = false
}

const hideModal = () => {
    show.value = false
    setTimeout(() => {
        useNuxtApp().$bus.$emit('hideModal', 'registration-modal')
    })
}

const showModal = (eventOpenType = 'onClick') => {
    show.value = true

    setTimeout(() => {
        useNuxtApp().$bus.$emit('showRegistrationModal', {
            eventOpenType,
        })
    })
}

const triggerOnScroll = () => {
    return document.documentElement.scrollTop > document.getElementsByTagName('body')[0].offsetHeight / 5
}

onMounted(() => {
    const registrationModalCookie = useRegistrationModalCookie()
    if (!registrationModalCookie.value.modalShown) {
        window.onscroll = () => {
            if (registrationModalStore.scrollBehaviorEnabled && triggerOnScroll()) {
                registrationModalCookie.value = {
                    ...registrationModalCookie.value,
                    modalShown: true,
                }
                showModal('onScroll')
            }
        }
    }

    document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
            hideModal()
        }
    })

    useNuxtApp().$bus.$off('showRegistrationModal')
    useNuxtApp().$bus.$on('showRegistrationModal', ({ eventOpenType = 'onClick' }: { eventOpenType: string }) => {
        useNuxtApp().$bus.$emit('showModal', 'registration-modal')

        DataLayer.push({
            event: 'pop_up',
            action: 'open',
            type: eventOpenType,
            pop_up_name: i18n.t('popUp.forNewCustomers') + ' ' + i18n.t('popUp.getDiscount').toLowerCase(),
            page_url: location.href,
        })
    })
})
</script>

<style lang="scss">
.registration-modal__close-box {
    position: fixed;
    overflow: visible;
    opacity: 1;
    border: 0;
    z-index: 7;
    transition-duration: 250ms;
    transition-timing-function: cubic-bezier(0.645, 0.045, 0.355, 1);
    transition-property: opacity, top, bottom;
    padding: 12px 14px 12px 14px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    background: rgba(255, 244, 242, 1);
    color: #000;
    box-shadow: 0 0 11px 0 rgba(0, 0, 0, 0.17);
    cursor: pointer;
    bottom: 5%;
    left: 1%;

    @media (max-width: 768px) {
        left: 50%;
        bottom: 90px;
        transform: translate(-50%);
    }
}

.registration-modal {
    &__body {
        display: flex;
        background: #fff4f2;
        border-radius: 10px;
    }

    &__image-wrapper {
        background-size: cover;
        background-position: center center;
        background-repeat: no-repeat;
        width: 70%;
        border-top-left-radius: 10px;
        border-bottom-left-radius: 10px;
    }

    .modal-content {
        padding: 0 !important;
        border-radius: 10px !important;
    }

    .modal-header {
        display: none !important;
    }

    &__main-nwl {
        text-align: start;
        width: 100%;
        padding: 10px 20px 15px 25px;
        background: rgba(255, 244, 242, 1);

        @media (min-width: 768px) {
            .registration-modal__title {
                padding-left: 16px;
                padding-right: 16px;
            }

            .registration-modal__text {
                padding: 16px;
            }

            .registration-modal__small-title {
                padding-left: 16px;
                padding-right: 16px;
            }
        }
    }

    &__title {
        margin-bottom: 15px;
        font-weight: 600;
        font-size: 30px;
        line-height: 42px;
    }

    &__text {
        font-size: 12px;
        line-height: 17px;
        margin-top: 15px;
    }

    &__small-title {
        font-size: 18px;
    }

    &__primary-button {
        background: #e6007e;
        border: 1px solid #e6007e;
        border-radius: 100px;
        color: #fff;
        display: inline-block;
        font-size: 14px;
        font-weight: 500;
        padding: 8px 20px;
        text-align: center;
        margin-top: 10px;
        margin-bottom: 10px;

        &:hover {
            background: #b80065;
            color: #fff;
            cursor: pointer;
        }
    }

    &__close {
        font-size: 0;
        width: 45px;
        height: 45px;
        position: relative;
        background-color: transparent;
        border: none;
        margin-right: -1rem;
        cursor: pointer;

        &::after {
            content: '';
            left: 0;
            top: 0;
            width: 45px;
            height: 45px;
            position: absolute;
            background: url('/nuxt-assets/icons/close.svg') no-repeat 50%;
        }
    }

    @media (max-width: 768px) {
        .modal-body {
            padding: 0 !important;
            height: auto !important;
        }

        &__primary-button {
            margin-bottom: 20px;
        }

        .modal-content {
            height: auto !important;
        }

        .modal-dialog {
            top: 25%;
        }
    }

    input {
        background-color: $color-white;
    }
}

.ico-open-modal {
    background: url('/nuxt-assets/images/icons/popUp/right-pink-arrow.svg') no-repeat 50%;
    background-size: contain;
    display: inline-block;
    min-height: 24px;
    min-width: 24px;
    margin-left: 10px !important;
}
</style>
